import { List as ImList, Map as ImMap, Record as ImmutableRecord, type Set as ImSet, type RecordOf } from 'immutable';
import _groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import { DateTime, Interval } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { shallowEqual } from 'react-redux';
import { createSelector } from 'reselect';

import { accountsBalancesSelectors } from 'app-components/flux/accounts-balances';
import { authSelectors } from 'app-components/flux/auth';
import { dateSelectors } from 'app-components/flux/date';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { createWeakMapSelector, resettableLruMemoize } from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import { getReadableDateRange } from 'app-components/utils/date';
import { constructPredefinedInterval, type Timestep } from 'app-components/utils/date/predefinedRange';
import type RootState from 'app-components/utils/redux-store/rootState';

import { accountNodeUtils } from '../account-nodes';
import { accountsSelectors, accountsUtils } from '../accounts';
import type { AccountingType, ChartView } from '../report-configurations/reportConfigurationsTypes';
import { transactionsSelectors } from '../transactions';
import { findSplitItemForReportCell, isZeroAmountOverflowSplit } from '../transactions/splitsHelpers';
import { mkTransactionItem, type SplitItemProps } from '../transactions/transactionsTypes';
import { createPlaceholderTxn } from '../transactions/transactionsUtils';
import { buildSankeyData, filterReportToSinglePeriod, type SankeyDataOptions } from './buildSankeyData';
import type { ComparisonChartCrumb } from './comparisonChartModel';
import { REPORT_WRAPPER_ENTITY_IDS, toComparisonChartData } from './comparisonChartModel';
import type { ComparisonColumnSource } from './comparisonColumnModel';
import { findCellForColumn, isComparisonReport } from './comparisonColumnModel';
import { toComparisonOverTimeData } from './comparisonOverTimeModel';
import reportsAdapter from './generatedReportsEntityAdapter';
import type { Breadcrumb, CellItem, NetWorthRow, Report, ReportCell, ReportColumn } from './generatedReportsTypes';
import { GENERATED_REPORTS_REDUCER_KEY, type ReportRow, type RowLabels } from './generatedReportsTypes';
import {
  getReconciliationAttemptKey,
  getReportCellNumberValueUtil,
  getReportCellValueUtil,
  getReportDrilldownTransactionIds,
  getReportEntityKey,
  getReportRowUtil,
  getReportTransactionIds,
  shouldHideExcludedZeroTotalReportRow,
} from './generatedReportsUtils';

type PieChartDataItem = {
  category: string;
  total: number;
  displayTotal?: number;
  categoryId: string;
  groupKey: string;
  originalRow: ReportRow;
  transactionIds: string[];
  isEverythingElse?: boolean;
  showSign?: 'positive' | 'negative';
};

type PieChartDataResult = {
  data: PieChartDataItem[];
  orderedFields: string[];
  transactionsBySegment: Record<string, string[]>;
};

type PieChartDataAccumulator = Omit<PieChartDataItem, 'total' | 'displayTotal' | 'showSign'> & {
  signedTotal: number;
};

const getTransactionSegmentId = (item: CellItem): string | undefined => item.splitItemId || item.txnId;

const collectTransactionSegmentIdsFromRow = (row: ReportRow, groupedRows?: Record<string, ReportRow[]>): string[] => {
  const segmentIds = new Set<string>();

  const collectFromRow = (currentRow: ReportRow): void => {
    let foundRowSegmentId = false;

    currentRow.cellData?.forEach((cell) => {
      cell.items?.forEach((item) => {
        const segmentId = getTransactionSegmentId(item);
        if (segmentId) {
          segmentIds.add(segmentId);
          foundRowSegmentId = true;
        }
      });
    });

    if (currentRow.entityType === 'TRANSACTION_TYPE' && currentRow.entityId && !foundRowSegmentId) {
      segmentIds.add(currentRow.entityId);
    }

    if (currentRow.id) {
      groupedRows?.[currentRow.id]?.forEach(collectFromRow);
    }
  };

  collectFromRow(row);

  return Array.from(segmentIds);
};

const addGraphSegmentIdFromCellItem = (item: CellItem, addSegmentId: (segmentId: string) => void): void => {
  const segmentId = getTransactionSegmentId(item);
  if (segmentId) {
    addSegmentId(segmentId);
  }
};

export const MAX_PIE_CHART_ITEMS = 11;

type PieChartDisplayPolicy = {
  transform: (signedTotal: number) => Pick<PieChartDataItem, 'displayTotal' | 'showSign' | 'total'>;
};

const defaultPieChartDisplayPolicy: PieChartDisplayPolicy = {
  transform: (signedTotal) => ({
    total: signedTotal,
    displayTotal: signedTotal,
    showSign: undefined,
  }),
};

const pieChartDisplayPolicyBySubtype: Partial<Record<NonNullable<Report['reportSubType']>, PieChartDisplayPolicy>> = {
  INCOME: {
    transform: (signedTotal) => ({
      total: Math.abs(signedTotal),
      displayTotal: signedTotal,
      showSign: signedTotal < 0 ? 'negative' : undefined,
    }),
  },
  SPENDING: {
    transform: (signedTotal) => ({
      total: Math.abs(signedTotal),
      displayTotal: Math.abs(signedTotal),
      showSign: signedTotal < 0 ? 'positive' : undefined,
    }),
  },
};

const getPieChartDisplayPolicy = (reportSubType: Report['reportSubType'] | undefined): PieChartDisplayPolicy =>
  (reportSubType ? pieChartDisplayPolicyBySubtype[reportSubType] : undefined) ?? defaultPieChartDisplayPolicy;

export const getPieChartDisplay = (
  reportSubType: Report['reportSubType'] | undefined,
  signedTotal: number,
): Pick<PieChartDataItem, 'displayTotal' | 'showSign' | 'total'> =>
  getPieChartDisplayPolicy(reportSubType).transform(signedTotal);

/**
 * Pie/donut visuals only render positive slice magnitudes after report-specific normalization. That excludes:
 * - $0 categories, including overflow-only split placeholders
 * - values that still normalize to a non-positive slice
 */
export const shouldRenderPieChartSlice = (total: number): boolean => Math.round(total * 100) > 0;

const pruneTransactionsBySegmentToPieCategories = (
  transactionsBySegment: Record<string, string[]>,
  pieChartData: PieChartDataItem[],
): Record<string, string[]> => {
  const shown = new Set(pieChartData.map((p) => p.category));
  return Object.fromEntries(Object.entries(transactionsBySegment).filter(([k]) => shown.has(k)));
};

export const formatColumnDateLabel = (
  columns: ReportColumn[],
  timestep: Timestep,
  startOn: string,
  endOn: string,
): string => {
  const todayYear = DateTime.local().year;
  let showYear: boolean | undefined;

  if (timestep === 'DAY') {
    const allCurrentYear = columns.every((col: ReportColumn) => {
      if (col?.headerType !== 'DATE_RANGE') return true;
      return DateTime.fromISO(col.headerValue.startOn).year === todayYear;
    });
    if (allCurrentYear) showYear = false;
  } else if (timestep !== 'YEAR') {
    const monthYears = new Map();
    const hasDuplicates = columns.some((col: ReportColumn) => {
      if (col?.headerType !== 'DATE_RANGE') return false;
      const { year, month } = DateTime.fromISO(col.headerValue.startOn);
      if (!monthYears.has(month) && todayYear === year) {
        monthYears.set(month, year);
        return false;
      }
      return monthYears.get(month) !== year || todayYear !== year;
    });
    if (hasDuplicates) showYear = true;
  }

  return getReadableDateRange(timestep, startOn, endOn, showYear);
};

export const getStore = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>) => state[GENERATED_REPORTS_REDUCER_KEY];
const reportsSelectors = reportsAdapter.getSelectors(getStore);

export const getDataVersion = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>) =>
  state[GENERATED_REPORTS_REDUCER_KEY].dataVersion;

export const getEntity = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>, { location }: { location: string }) =>
  reportsSelectors.selectById(state, location);

export const getActiveViewingConfigId = createSelector(getStore, (store) => store.activeViewingConfigId);

export const getIsGenerating = createSelector(getEntity, (entity) => entity?.isGenerating);
export const getReport = createSelector(getEntity, (entity) => entity?.report);
export const getError = createSelector(getEntity, (entity) => entity?.error);
export const getGeneratedAtVersion = createSelector(getEntity, (entity) => entity?.generatedAtVersion);
export const getRelevantReportTransactionIds = createSelector(
  getReport,
  (_state: unknown, props: { transactionIds: readonly string[] }) => props.transactionIds,
  (report, transactionIds): string[] => getReportTransactionIds(report, new Set(transactionIds)),
);
export const getMissingReportTransactionIds = createSelector(
  getRelevantReportTransactionIds,
  transactionsSelectors.getResourcesById,
  (transactionIds, transactionsById): string[] =>
    transactionIds.filter((transactionId) => !transactionsById.has(transactionId)),
);
/** A success action can contain failed outcomes; only confirmed absence settles a missing resource. */
export const getIsReportTransactionReconciliationSettled = createSelector(
  getEntity,
  getRelevantReportTransactionIds,
  getMissingReportTransactionIds,
  (entity, transactionIds, missingIds): boolean => {
    if (!entity?.report || entity.isGenerating) return false;
    if (missingIds.length === 0) return true;

    const reconciliation = entity.transactionReconciliations?.[getReconciliationAttemptKey(transactionIds)];
    if (!reconciliation || reconciliation.generation !== entity.generation) return false;
    if (reconciliation.status === 'loading' || reconciliation.status === 'failed') return false;

    const absentIds = new Set(reconciliation.outcomes.filter(({ status }) => status === 'absent').map(({ id }) => id));
    return missingIds.every((id) => absentIds.has(id));
  },
);
export const getDrilldownTransactionIds = createSelector(
  getReport,
  (_state: unknown, props: { columnId?: string }) => props.columnId,
  (_state: unknown, props: { entityId?: string }) => props.entityId,
  (_state: unknown, props: { rowId?: string }) => props.rowId,
  (report, columnId, entityId, rowId): string[] =>
    getReportDrilldownTransactionIds(report, { columnId, entityId, rowId }),
);
export const getAmountOnById = createSelector(
  getReport,
  (
    _state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>,
    { transactionIds }: { location: string; transactionIds?: ImSet<string> | null },
  ) => transactionIds,
  (report, transactionIds) => {
    const map = new Map<string, string>();
    if (!report?.rows) {
      return map;
    }
    report.rows.forEach((row: ReportRow) => {
      row.cellData?.forEach((cell) => {
        cell.items?.forEach((item) => {
          const id = item.splitItemId || item.txnId;
          if (!id) return;
          if (transactionIds && !transactionIds.has(id)) return;
          if (item.amountOn) {
            map.set(id, item.amountOn);
          }
        });
      });
    });
    return map;
  },
);

const defaultRowLabels: RowLabels = {
  positiveLabel: 'INCOME',
  negativeLabel: 'EXPENSE',
  combinedLabel: 'NET_INCOME',
};
// Net worth and reports
export const getGraphDataFromReport = createSelector(
  getReport,
  (_state: unknown, props: { timestep?: Timestep }) => props.timestep,
  (_state: unknown, props: { rowLabels?: RowLabels }) => props.rowLabels,
  (report, timestep = 'MONTH', rowLabels = defaultRowLabels) => {
    // Comparison reports are grid-only in v1. Their columns are grouped and include derived
    // (cumulative / delta) columns, so pairing cells to columns positionally below would emit
    // phantom series and plot a percentage on a dollar axis — and the headerType switch throws
    // outright on anything it does not recognise.
    if (isComparisonReport(report)) {
      return null;
    }

    const rowsByParentId = _groupBy(report?.rows, ({ parentId }) => parentId ?? '-1');
    const graphRows = rowsByParentId['-1'] ?? [];
    const cols = report?.columns ?? [];
    if (graphRows) {
      const income = graphRows?.find((row) => row?.entityId === rowLabels.positiveLabel);
      const spending = graphRows?.find((row) => row?.entityId === rowLabels.negativeLabel);
      const combined = graphRows?.find((row) => row?.entityId === rowLabels.combinedLabel);

      const graphData = cols[cols.length - 1]?.isAggSum ? graphRows[0]?.cellData.slice(0, -1) : graphRows[0]?.cellData;

      return graphData?.reduce<
        {
          positiveTotal: number;
          negativeTotal: number;
          combinedTotal: number;
          timestep: Timestep;
          category: string;
          startOn?: string;
          endOn?: string;
        }[]
      >((acc, _data, index: number) => {
        const column = cols[index];
        let combinedTotal = 0;
        if (rowLabels?.combinedLabel) {
          // use combined amount
          combinedTotal = combined ? Number(combined?.cellData?.[index].value) : 0;
        } else {
          combinedTotal =
            income || spending
              ? // spending is already negative, so this '+' actually subtracts for the new value
                Number(income?.cellData?.[index]?.value ?? 0) + Number(spending?.cellData?.[index]?.value ?? 0)
              : 0;
        }

        // Renamed on web and mobile.
        // Both totals keep the sign the report gave them, so spending stays negative in the normal
        // case and plots below the zero line, while still going positive when refunds outweigh it.
        const totals = {
          positiveTotal: income ? Number(income?.cellData?.[index]?.value ?? 0) : 0,
          negativeTotal: spending ? Number(spending?.cellData?.[index]?.value ?? 0) : 0,
          combinedTotal,
          timestep,
        };

        let result;
        if (column?.headerType === 'QCS_ENTITY') {
          result = {
            category:
              column.headerValue.qcs_entity === 'ACCOUNT'
                ? accountsUtils.getAccountString(column.headerValue.id) || 'Unknown'
                : column.headerValue.id,
            ...totals,
          };
        } else if (column?.headerType === 'DATE_RANGE') {
          result = {
            category: formatColumnDateLabel(cols, timestep, column.headerValue.startOn, column.headerValue.endOn),
            startOn: column?.headerValue?.startOn,
            endOn: column?.headerValue?.endOn,
            ...totals,
          };
        } else if (column?.headerType === 'STRING') {
          result = {
            category: column.headerValue.id,
            ...totals,
          };
        } else {
          throw new Error(`Expected column type of DATE_RANGE, QCS_ENTITY, or STRING`);
        }

        acc.push(result);
        return acc;
      }, []);
    }
    return null;
  },
);

/**
 * Selector to get bar chart data from a report for BAR_GROUPED and BAR_STACKED charts
 * Returns data formatted following reportsSelectors pattern with ImmutableMap structure
 * Compatible with getReportForDisplay wrapper and maintains drill-down support
 */
// Aliases of the one definition in comparisonChartModel. These were three independent copies of the
// same eight ids; the names are kept because each reads correctly at its own call site.
//
// A fourth near-copy in spending-dashboard's spendingFlowSankeyCompute.ts is deliberately NOT folded in: it also
// lists `PERSONAL_INCOME` and `BUSINESS_INCOME`, so aliasing it would silently drop two ids.
const BAR_TOP_LEVEL_WRAPPER_ENTITY_IDS = REPORT_WRAPPER_ENTITY_IDS;

/**
 * Empty result for the bar chart. Built fresh per call rather than shared, matching the existing
 * behaviour — the inner `chartData` holds plain mutable arrays, so a shared instance could be
 * poisoned by any consumer that mutates what it is handed.
 */
const emptyBarChartData = () =>
  ImMap({
    chartData: { data: [] as Record<string, any>[], orderedFields: [] as string[] },
    transactionsBySegment: {} as Record<number, string[]>,
    total: 0,
    rawReport: null,
  });

export const getBarChartDataFromReport = createSelector(
  getReport,
  (_state: unknown, props: { timestep?: Timestep; rowLabels?: RowLabels }) => props.timestep,
  (_state: unknown, props: { depth?: number }) => props.depth,
  (report, timestep = 'MONTH', depth?: number) => {
    const rowsByParentId = _groupBy(report?.rows, ({ parentId }) => parentId ?? '-1');
    const topLevelRows = rowsByParentId['-1'] ?? [];
    const cols = report?.columns ?? [];
    // Comparison reports are grid-only in v1. Beyond the phantom-series problem, this selector
    // resolves cells with `cell.columnId === index.toString()`, i.e. it treats columnId as the
    // ordinal — comparison columnIds are range-scoped strings, so every bar would render 0.
    if (isComparisonReport(report) || !topLevelRows.length || !cols.length) {
      return emptyBarChartData();
    }

    // Get the data columns (excluding aggregated sum if present)
    const graphData = cols.at(-1)?.isAggSum ? cols.slice(0, -1) : cols;
    const meaningfulTopRows = topLevelRows.filter((row) => row.entityId !== 'TOTAL');
    const shouldFlipSign = report?.reportSubType === 'SPENDING';

    // Find categories at the specified level
    const findCategoriesAtLevel = (level: number): ReportRow[] => {
      const categories: ReportRow[] = [];

      // Helper function to traverse to the target level
      const traverseToLevel = (currentRows: ReportRow[], currentLevel: number, parentPath: string[] = []): void => {
        currentRows.forEach((row) => {
          if (row.entityId === 'TOTAL') return;

          if (currentLevel === level) {
            // We've reached the target level, include this row
            categories.push(row);
            return;
          }

          // Not at target level yet, go deeper
          const children = row.id ? rowsByParentId[row.id] || [] : [];
          if (children.length > 0) {
            // Has children, traverse deeper
            traverseToLevel(children, currentLevel + 1, [
              ...parentPath,
              row.name || row.entityId || row.id || 'unknown',
            ]);
          }
        });
      };

      // Start traversal from top-level rows (Level 0)
      traverseToLevel(meaningfulTopRows, 0);

      // If no categories found at target level, use fallback logic
      if (categories.length === 0) {
        // Try one level up
        if (level > 0) {
          return findCategoriesAtLevel(level - 1);
        } else {
          // At level 0 already, use top-level rows
          return meaningfulTopRows;
        }
      }

      return categories;
    };

    const getDefaultBarRows = (): ReportRow[] => {
      let currentRows = meaningfulTopRows;

      while (currentRows.length > 0) {
        const wrapperRows = currentRows.filter((row) =>
          BAR_TOP_LEVEL_WRAPPER_ENTITY_IDS.has((row.entityId || '').toUpperCase()),
        );

        if (!wrapperRows.length) {
          return currentRows;
        }

        const nextRows = wrapperRows.flatMap((row) =>
          (row.id ? rowsByParentId[row.id] || [] : []).filter((child) => child.entityId !== 'TOTAL'),
        );

        if (!nextRows.length) {
          return currentRows;
        }

        currentRows = nextRows;
      }

      return meaningfulTopRows;
    };

    const dataRows = depth !== undefined ? findCategoriesAtLevel(depth) : getDefaultBarRows();

    // Transform data for bar chart format and collect transaction IDs per segment
    const transactionsBySegment: Record<number, string[]> = {};
    const barChartData = graphData?.reduce<Record<string, any>[]>((acc, column, index: number) => {
      if (!column) return acc;

      // Initialize transaction data for this time period
      transactionsBySegment[index] = [];

      // Build the data point for this time period
      const dataPoint: Record<string, any> = {};

      // Add data for each category row, aggregating duplicate category names
      const categoryTotals: Record<string, number> = {};

      dataRows.forEach((row) => {
        const fieldName = row.name || row.entityId || row.id || 'unknown';
        let value = 0;

        // Handle missing cellData gracefully
        // Find the cell that corresponds to this time period column
        const cellForThisPeriod = row.cellData?.find((cell: ReportCell) => cell.columnId === index.toString());

        if (cellForThisPeriod && cellForThisPeriod.value !== undefined && cellForThisPeriod.value !== null) {
          value = Number(cellForThisPeriod.value);
          if (shouldFlipSign) {
            value = -1 * value;
          }
          if (Object.is(value, -0)) {
            value = 0;
          }
          // Collect transaction IDs from this cell for this specific time period
          if (cellForThisPeriod.items && Array.isArray(cellForThisPeriod.items)) {
            const segmentSet = new Set(transactionsBySegment[index]);
            cellForThisPeriod.items.forEach((item: CellItem) => {
              addGraphSegmentIdFromCellItem(item, (segmentId) => {
                segmentSet.add(segmentId);
              });
            });
            transactionsBySegment[index] = Array.from(segmentSet);
          }
        }

        // Aggregate values for duplicate category names
        categoryTotals[fieldName] = (categoryTotals[fieldName] || 0) + value;
      });

      // Add aggregated category totals to data point
      Object.entries(categoryTotals).forEach(([fieldName, totalValue]) => {
        dataPoint[fieldName] = totalValue;
      });

      // Determine category based on column type
      if (column?.headerType === 'QCS_ENTITY') {
        dataPoint.category =
          column.headerValue.qcs_entity === 'ACCOUNT'
            ? accountsUtils.getAccountString(column.headerValue.id) || 'Unknown'
            : column.headerValue.id;
      } else if (column?.headerType === 'DATE_RANGE') {
        dataPoint.category = formatColumnDateLabel(
          cols,
          timestep,
          column.headerValue.startOn,
          column.headerValue.endOn,
        );
        dataPoint.startOn = column?.headerValue?.startOn;
        dataPoint.endOn = column?.headerValue?.endOn;
      } else if (column?.headerType === 'STRING') {
        dataPoint.category = column.headerValue.id;
      } else {
        dataPoint.category = `Column ${index + 1}`;
      }

      acc.push(dataPoint);
      return acc;
    }, []);

    // Build ordered fields array - for bar charts, these should be the data series names (category names)
    // The BarChart component creates a series for each field in orderedFields
    const orderedFields = Array.from(new Set(dataRows.map((row) => row.name || row.entityId).filter(Boolean)));

    // Time periods are extracted from the data.category field in each data point
    // const timePeriods: string[] = barChartData?.map(item => item.category).filter(Boolean) || [];

    // Calculate total across all data rows and columns
    let total = 0;
    dataRows.forEach((row) => {
      if (row.cellData) {
        const rowTotal = row.cellData.reduce((sum: number, cell: ReportCell) => sum + Number(cell.value || 0), 0);
        total += rowTotal;
      }
    });

    return ImMap({
      chartData: {
        data: barChartData || [],
        orderedFields,
      },
      transactionsBySegment,
      total,
      rawReport: report,
    });
  },
);

export const getReportTransactionItems = createSelector(
  [
    getReport,
    transactionsSelectors.getResourcesById,
    transactionsSelectors.getTransactionItemsById,
    (_state: unknown, props: { rowId?: string }) => props.rowId,
    (_state: unknown, props: { entityId?: string }) => props.entityId,
    // Clamped rather than left as NaN: reselect's default reference equality never matches NaN
    // against itself, so a comparison columnId would defeat memoization and hand every subscriber a
    // fresh list on each store notification.
    (_state: unknown, props: { columnId?: string }) => {
      const ordinal = Number(props.columnId ?? -1);
      return Number.isNaN(ordinal) ? -1 : ordinal;
    },
    // The raw id as well as the ordinal. An ordinary report's column ids are positional ("0", "1",
    // …) so `Number()` is a valid index, but a comparison report's are opaque strings and coerce to
    // NaN — and `Array.prototype.at(NaN)` silently returns element 0, i.e. the first period's
    // transactions, for every column the user drills into.
    (_state: unknown, props: { columnId?: string }) => props.columnId,
    (_state: unknown, props: { accountingType: AccountingType }) => props.accountingType,
    (_state: unknown, props: { businessId?: string }) => props.businessId,
  ],
  (
    report,
    transactionsById,
    transactionItemsById,
    rowID,
    entityId = 'NET_INCOME',
    columnId,
    rawColumnId,
    accountingType,
    businessId,
  ) => {
    // `report.reportType` falls back to 'TRANSACTION' when QCS omits it (see the transformer), so a
    // comparison response can claim to be a transaction report and take the branch below — which
    // looks for TRANSACTION_TYPE rows, finds none among the category rows, and returns nothing.
    const isComparison = isComparisonReport(report);

    // Handle transaction reports differently - they have transactions as rows, not in cell items
    if (report?.reportType === 'TRANSACTION' && !isComparison) {
      // Handle top-level case: special value to show ALL transactions
      if (entityId === 'SHOW_ALL_TRANSACTIONS') {
        // Find all transaction rows in the entire report
        const allTransactionRows = report?.rows.filter((row) => row.entityType === 'TRANSACTION_TYPE') || [];

        // Convert transaction rows to TransactionItems
        const transactionItems = allTransactionRows.reduce<any[]>((acc, row) => {
          if (!row.entityId) return acc;
          const item = transactionItemsById.get(row.entityId);
          if (item) acc.push(item);
          return acc;
        }, []);

        return ImList(transactionItems);
      }

      // Find the category row first
      const categoryRow = report?.rows.find((row) => row.entityId === entityId);

      if (!categoryRow) {
        return ImList([]);
      }

      // Find all transaction rows that belong to the selected category
      const findTransactionRowsForCategory = (categoryEntityId: string): ReportRow[] => {
        const transactionRows: ReportRow[] = [];

        // Find all rows that are children of this category (recursively)
        const findChildTransactions = (parentEntityId: string) => {
          const categoryRow = report?.rows.find((row) => row.entityId === parentEntityId);

          if (!categoryRow?.childrenEntityIds) return;

          categoryRow.childrenEntityIds.forEach((childEntityId) => {
            const childRow = report?.rows.find((row) => row.entityId === childEntityId);

            if (!childRow) return;

            if (childRow.entityType === 'TRANSACTION_TYPE') {
              // This is a transaction row - add it to our results
              transactionRows.push(childRow);
            } else if (childRow.entityType === 'CATEGORY_TYPE') {
              // This is a subcategory - recurse into it
              findChildTransactions(childEntityId);
            }
          });
        };

        findChildTransactions(categoryEntityId);
        return transactionRows;
      };

      const transactionRows = findTransactionRowsForCategory(entityId);

      // Convert transaction rows to TransactionItems
      const transactionItems = transactionRows.reduce<any[]>((acc, row) => {
        if (!row.entityId) return acc;
        const item = transactionItemsById.get(row.entityId);
        if (item) acc.push(item);
        return acc;
      }, []);

      return ImList(transactionItems);
    }

    // tax report could contain multiple businesses, so we need to only the the relevate items for the specified business
    const getRowForBusinessId = (businessId: string): ReportRow | undefined => {
      // recusively find the parent node and return true if it matches the business id, otherwise return false
      const isCorrectBusiness = (candidateRow: ReportRow): boolean => {
        if (candidateRow.parentId === '-1') {
          return candidateRow.entityId === businessId;
        } else {
          const parentRow = report?.rows.find((row) => row.id === candidateRow.parentId);
          return parentRow ? isCorrectBusiness(parentRow) : false;
        }
      };
      // find all of the matching tax line item rows
      const candiates = report?.rows.filter((row) => (entityId ? row.entityId === entityId : row.id === rowID));
      // return the row matching corresonding to the specific business
      if (candiates && candiates.length > 0) {
        for (let i = 0; i < candiates.length; i++) {
          if (isCorrectBusiness(candiates[i])) {
            return candiates[i];
          }
        }
      }
      return;
    };

    // check rowId first (more specific), then fallback to entityId
    const getRow = () => report?.rows.find((row) => (rowID ? row.id === rowID : row.entityId === entityId));
    const row = businessId ? getRowForBusinessId(businessId) : getRow();

    const cellForColumn = (currentRow: ReportRow | undefined, ordinal: number) =>
      findCellForColumn(currentRow, isComparison ? rawColumnId : ordinal, isComparison);

    // Helper function to recursively collect all transaction items from a row and its descendants
    const collectAllTransactionItems = (currentRow: ReportRow, columnId: number): CellItem[] => {
      const items: CellItem[] = [];

      // Get direct items from this row
      const directItems = cellForColumn(currentRow, columnId)?.items || [];
      items.push(...directItems);

      // Get child rows and recursively collect their items
      const childRows = report?.rows.filter((r: ReportRow) => r.parentId === currentRow.id) || [];
      for (const childRow of childRows) {
        const childItems = collectAllTransactionItems(childRow, columnId);
        items.push(...childItems);
      }

      return items;
    };

    // Check if this is a total row
    const isTotal = row?.isTotal;

    let allItems: CellItem[] = [];

    if (isTotal && row) {
      // For total rows, recursively aggregate transactions from all descendant entities in the same column
      allItems = collectAllTransactionItems(row, columnId);
    } else {
      // For regular rows, use the existing logic
      allItems = cellForColumn(row, columnId)?.items || [];
    }

    return ImList(
      allItems.reduce((acc: any[], cellItem: CellItem) => {
        // Try to get existing transaction item first
        if (cellItem.vehicleId) {
          return acc; // Skip mileage items here
        }
        const itemId = cellItem?.splitItemId || cellItem.txnId;
        if (!itemId) return acc;
        let result = transactionItemsById.get(itemId);

        if (result) {
          // If we have accounting type CASH and need to preserve special logic, modify the existing item
          if (accountingType === 'CASH') {
            const txn = cellItem.txnId ? transactionsById.get(cellItem.txnId) : null;
            if (txn && ImmutableRecord.isRecord(txn)) {
              const split = findSplitItemForReportCell(txn, cellItem);
              const isPartial = (split?.amount || txn?.amount) !== cellItem.amount;

              // Create a modified version with accounting-specific data
              result = mkTransactionItem({
                transaction: txn,
                splitItem: split as RecordOf<SplitItemProps>,
                item: {
                  ...txn.toObject(),
                  ...(ImmutableRecord.isRecord(split) ? split?.toObject() : split),
                  isPartial,
                  unpaidAmount: isPartial ? (split?.amount || txn?.amount) - cellItem.amount : undefined,
                  paidAmount: cellItem.amount,
                  paymentTxnId: cellItem?.paymentTxnId,
                  postedOn: cellItem?.amountOn,
                },
              });
            }
          }
        } else {
          // Fallback for missing items - check if transaction exists
          const txn = cellItem.txnId ? transactionsById.get(cellItem.txnId) : null;
          if (txn) {
            if (!ImmutableRecord.isRecord(txn)) {
              assert(false, 'transaction is not a record in transactionsById');
              result = createPlaceholderTxn(
                txn.payee || 'N/A',
                cellItem.amount || 0,
                cellItem.amountOn || '',
                cellItem.txnId || '',
                cellItem.splitItemId,
              );
            } else {
              const split = findSplitItemForReportCell(txn, cellItem);
              const isPartial = (split?.amount || txn?.amount) !== cellItem.amount;
              result = mkTransactionItem({
                transaction: txn,
                splitItem: split as RecordOf<SplitItemProps>,
                item: {
                  ...txn.toObject(),
                  ...(ImmutableRecord.isRecord(split) ? split?.toObject() : split),
                  ...(accountingType === 'CASH' && {
                    isPartial,
                    unpaidAmount: isPartial ? (split?.amount || txn?.amount) - cellItem.amount : undefined,
                    paidAmount: cellItem.amount,
                    paymentTxnId: cellItem?.paymentTxnId,
                    postedOn: cellItem?.amountOn,
                  }),
                },
              });
            }
          } else {
            result = createPlaceholderTxn(
              'NA',
              cellItem.amount || 0,
              cellItem.amountOn || '',
              cellItem.txnId || '',
              cellItem.splitItemId,
            );
          }
        }

        if (result) {
          // Cash reports can display a different amount from the persisted split.
          if (isZeroAmountOverflowSplit({ isOverflow: result.splitItem?.isOverflow, amount: cellItem.amount })) {
            return acc;
          }
          acc.push(result);
        }
        return acc;
      }, []),
    );
  },
);

export const rowsByParentId = createSelector(getReport, (report) =>
  _groupBy(report?.rows, (row) => row.parentId ?? '-1'),
);

type SankeyDataProps = {
  location: string;
  showSubcategory?: boolean;
  layout?: SankeyDataOptions['layout'];
};

// Sankey flow data for NET_INCOME reports (Profit & Loss, Income & Expenses).
// Derived purely from the generated report — see buildSankeyData for the shape.
export const getSankeyDataFromReport = createWeakMapSelector(
  [
    getReport,
    (_state: unknown, props?: SankeyDataProps): boolean => props?.showSubcategory ?? true,
    (_state: unknown, props?: SankeyDataProps): SankeyDataOptions['layout'] => props?.layout,
  ],
  (report, showSubcategory, layout) => buildSankeyData(report, { showSubcategory, layout }),
);

type SankeyPeriodProps = { location: string; startOn?: string | null; endOn?: string | null };

// Like getSankeyDataFromReport but slices a TIME-bucketed report to a single
// DATE_RANGE column before building Sankey data. Falls back to the full report
// when startOn/endOn are absent (no period selected yet).
export const getSankeyDataForPeriod = createWeakMapSelector(
  [
    getReport,
    (_state: unknown, props: SankeyPeriodProps) => props.startOn,
    (_state: unknown, props: SankeyPeriodProps) => props.endOn,
  ],
  (report, startOn, endOn) => {
    if (startOn && endOn) {
      return buildSankeyData(filterReportToSinglePeriod(report, startOn, endOn), {
        showSubcategory: false,
        showIncomeBreakdown: false,
      });
    }
    return buildSankeyData(report, { showSubcategory: false, showIncomeBreakdown: false });
  },
);

// Per-category comparison bar chart data for COMPARISON reports. Derived purely from the generated
// report — see comparisonChartModel for the shape and the aggregation rules.
//
// WeakMap-memoized rather than lru(1): two report locations can be mounted at once (a saved report
// and a drill-down), and a size-1 cache would recompute on every alternating read.
//
// Parameterised by the drill-down breadcrumbs, so a bar click re-levels the chart to the clicked
// category's children the way a pie slice click does. Pass the same array instance across renders —
// the WeakMap memoization keys on reference identity, so a freshly built array recomputes.
export const getComparisonChartData = createWeakMapSelector(
  [
    getReport,
    (
      _state: unknown,
      props: {
        location: string;
        breadcrumbs?: readonly ComparisonChartCrumb[];
        comparisonColumnSource?: ComparisonColumnSource;
        periodGroupId?: string;
      },
    ) => props.breadcrumbs,
    (_state: unknown, props: { location: string; comparisonColumnSource?: ComparisonColumnSource }) =>
      props.comparisonColumnSource,
    (_state: unknown, props: { location: string; periodGroupId?: string }) => props.periodGroupId,
  ],
  (report, breadcrumbs, comparisonColumnSource, periodGroupId) =>
    toComparisonChartData(report, { breadcrumbs, comparisonColumnSource, periodGroupId }),
);

/** Full-period cumulative totals for comparison reports. Depends only on the report identity. */
export const getComparisonOverTimeData = createWeakMapSelector([getReport], toComparisonOverTimeData);

/**
 * Enhanced selector that wraps getGraphDataFromReport and adds transactionsBySegment
 * for optimized drill-down performance while maintaining backwards compatibility
 */
export const getGraphDataFromReportWithTransactions = createSelector(
  getGraphDataFromReport,
  getReport,
  rowsByParentId,
  transactionsSelectors.getResourcesById,
  (_state: unknown, props: { timestep?: Timestep }) => props.timestep,
  (_state: unknown, props: { rowLabels?: RowLabels }) => props.rowLabels,
  (originalGraphData, report, groupedRows, txnsById, _timestep = 'MONTH', _rowLabels = defaultRowLabels) => {
    if (!originalGraphData || !report?.rows) {
      return null;
    }

    // Collect transaction IDs by time segment (column index)
    const transactionsBySegment: Record<string, string[]> = {};

    const cols = report?.columns ?? [];
    const graphRows = groupedRows['-1'] ?? [];

    // Find the income and spending rows
    const income = graphRows?.find((row) => row?.entityId === _rowLabels.positiveLabel);
    const spending = graphRows?.find((row) => row?.entityId === _rowLabels.negativeLabel);

    // Helper function to collect transaction IDs from a row's children for a specific column
    const collectTransactionIdsForColumn = (row: ReportRow, columnIndex: number): string[] => {
      const transactionIdSet = new Set<string>();

      if (!row?.id || !groupedRows[row.id]) {
        return [];
      }

      // Get children of this row (the actual transaction items or subcategories)
      const children = groupedRows[row.id];

      children.forEach((child: ReportRow) => {
        // For TRANSACTION_TYPE rows, use the entityId as transaction ID
        if (child.entityType === 'TRANSACTION_TYPE' && child.entityId) {
          // Check if this transaction has a value in the specified column
          const cellValue = child.cellData?.[columnIndex]?.value;
          if (cellValue && !isNaN(Number(cellValue)) && Number(cellValue) !== 0) {
            transactionIdSet.add(child.entityId);
          }
        }

        // Check cell items for transaction IDs (fallback method)
        const cell = child.cellData?.[columnIndex];
        if (cell?.items && Array.isArray(cell.items)) {
          cell.items.forEach((item: CellItem) => {
            addGraphSegmentIdFromCellItem(item, (segmentId) => {
              transactionIdSet.add(segmentId);
            });
          });
        }

        // Recursively check grandchildren
        if (child.id && groupedRows[child.id]) {
          const grandchildren = groupedRows[child.id];
          grandchildren.forEach((grandchild: ReportRow) => {
            if (grandchild.entityType === 'TRANSACTION_TYPE' && grandchild.entityId) {
              const grandchildCellValue = grandchild.cellData?.[columnIndex]?.value;
              if (grandchildCellValue && !isNaN(Number(grandchildCellValue)) && Number(grandchildCellValue) !== 0) {
                transactionIdSet.add(grandchild.entityId);
              }
            }

            const grandchildCell = grandchild.cellData?.[columnIndex];
            if (grandchildCell?.items && Array.isArray(grandchildCell.items)) {
              grandchildCell.items.forEach((item: CellItem) => {
                addGraphSegmentIdFromCellItem(item, (segmentId) => {
                  transactionIdSet.add(segmentId);
                });
              });
            }
          });
        }
      });

      return Array.from(transactionIdSet);
    };

    // Collect transaction IDs for each column (time period)
    cols.forEach((column, columnIndex) => {
      const allTransactionIds: string[] = [];

      // Collect from income row
      if (income) {
        const incomeTransactions = collectTransactionIdsForColumn(income, columnIndex);
        allTransactionIds.push(...incomeTransactions);
      }

      // Collect from spending row
      if (spending) {
        const spendingTransactions = collectTransactionIdsForColumn(spending, columnIndex);
        allTransactionIds.push(...spendingTransactions);
      }

      // Store transactions by column index
      if (allTransactionIds.length > 0) {
        transactionsBySegment[columnIndex.toString()] = [...new Set(allTransactionIds)]; // Remove duplicates
      }
    });

    return {
      data: originalGraphData,
      transactionsBySegment,
    };
  },
  {
    memoize: resettableLruMemoize,
    argsMemoize: resettableLruMemoize,
    memoizeOptions: {
      maxSize: 10,
      resultEqualityCheck: shallowEqual,
      equalityCheck: isEqual,
    },
  },
);

export const netWorthEarliestStartDate = createSelector(
  authSelectors.getCurrentDataset,
  accountsBalancesSelectors.getOldestAvailableBalanceDate,
  (dataset, oldestBalanceDate) => {
    const oldestDateString = oldestBalanceDate;
    const createdAtDateString = dataset?.createdAt;

    let earliestDateString = oldestDateString ?? createdAtDateString ?? null;
    if (oldestDateString && createdAtDateString) {
      earliestDateString = oldestDateString < createdAtDateString ? oldestDateString : createdAtDateString;
    }

    if (!earliestDateString) {
      return DateTime.fromISO('').startOf('day');
    }

    const earliestDate = DateTime.fromISO(earliestDateString).startOf('day');
    return earliestDate.isValid ? earliestDate : DateTime.fromISO('').startOf('day');
  },
);

export const getEarliestStartDateToTodayTimeDifference = createSelector(
  netWorthEarliestStartDate,
  dateSelectors.getCurrentDate,
  (earliestStartDate, today) => {
    const days = today?.diff(earliestStartDate, 'days')?.days ?? 30;
    return days;
  },
);

export const getEarliestStartDateToTodayInterval = createSelector(
  dateSelectors.getCurrentDate,
  netWorthEarliestStartDate,
  (today, earliestStartDate) => {
    const allTimeDateRange = Interval.fromDateTimes(earliestStartDate, today);
    return allTimeDateRange;
  },
);

export const getTimestepForLineChart = createSelector(getEarliestStartDateToTodayTimeDifference, (dayDifference) => {
  if (dayDifference < 31) {
    return 'DAY';
  } else if (dayDifference < 90) {
    return 'DAY';
  } else if (dayDifference < 180) {
    return 'DAY';
  } else if (dayDifference < 365) {
    return 'DAY';
  } else if (dayDifference < 1825) {
    return 'MONTH';
  } else {
    return 'MONTH';
  }
});

export const getTimestepForBarChart = createSelector(getEarliestStartDateToTodayTimeDifference, (dayDifference) => {
  if (dayDifference < 31) {
    return 'DAY';
  } else if (dayDifference < 90) {
    return 'MONTH';
  } else if (dayDifference < 180) {
    return 'MONTH';
  } else if (dayDifference < 365) {
    return 'MONTH';
  } else if (dayDifference < 1825) {
    return 'QUARTER';
  } else {
    return 'YEAR';
  }
});

export const getDateRangeOptions = createSelector(
  getTimestepForBarChart,
  getTimestepForLineChart,
  dateSelectors.getCurrentDate,
  getEarliestStartDateToTodayInterval,
  (barChartStep, lineChartStep, currentDate, allTimeInterval) => {
    return [
      {
        label: '1M',
        longLabel: '1 month',
        id: 'SAME_DAY_LAST_MONTH' as const,
        timestep: 'DAY',
        assetDebtTimestep: 'DAY',
        interval: constructPredefinedInterval({ value: 'SAME_DAY_LAST_MONTH' }, currentDate),
      },
      {
        label: '3M',
        longLabel: '3 months',
        id: 'SAME_DAY_LAST_3_MONTHS' as const,
        timestep: 'DAY',
        assetDebtTimestep: 'MONTH',
        interval: constructPredefinedInterval({ value: 'SAME_DAY_LAST_3_MONTHS' }, currentDate),
      },
      {
        label: '6M',
        longLabel: '6 months',
        id: 'SAME_DAY_LAST_6_MONTHS' as const,
        timestep: 'DAY',
        assetDebtTimestep: 'MONTH',
        interval: constructPredefinedInterval({ value: 'SAME_DAY_LAST_6_MONTHS' }, currentDate),
      },
      {
        label: '1Y',
        longLabel: '1 year',
        id: 'SAME_DAY_LAST_YEAR' as const,
        timestep: 'DAY',
        assetDebtTimestep: 'MONTH',
        interval: constructPredefinedInterval({ value: 'SAME_DAY_LAST_YEAR' }, currentDate),
      },
      {
        label: '5Y',
        longLabel: '5 years',
        id: 'SAME_DAY_LAST_5_YEARS' as const,
        timestep: 'MONTH',
        assetDebtTimestep: 'QUARTER',
        interval: constructPredefinedInterval({ value: 'SAME_DAY_LAST_5_YEARS' }, currentDate),
      },
      {
        label: 'QTD',
        longLabel: 'Quarter to date',
        id: 'QUARTER_TO_DATE' as const,
        timestep: 'DAY',
        assetDebtTimestep: 'MONTH',
        interval: constructPredefinedInterval({ value: 'QUARTER_TO_DATE' }, currentDate),
      },
      {
        label: 'YTD',
        longLabel: 'Year to date',
        id: 'YEAR_TO_DATE' as const,
        timestep: 'DAY',
        assetDebtTimestep: 'MONTH',
        interval: constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, currentDate),
      },
      {
        label: 'All',
        longLabel: 'All time',
        id: 'ALL_DATES' as const,
        timestep: lineChartStep,
        assetDebtTimestep: barChartStep,
        interval: allTimeInterval,
      },
    ];
  },
);

export const getReportRow = createCachedSelector(
  getReport,
  (_state: unknown, props: { entityType?: string }) => props.entityType,
  (_state: unknown, props: { entityId?: string }) => props.entityId,
  (_state: unknown, props: { parentId?: string }) => props.parentId,
  getReportRowUtil,
)({
  keySelector: (state, props) => `${props.location}:${props.entityType}:${props.entityId}:${props.parentId}` || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 400 }),
});

export const getReportCellValue = createCachedSelector(
  getReport,
  (_state: unknown, props: { columnHeaderId?: string }) => props.columnHeaderId,
  (_state: unknown, props: { entityType?: string }) => props.entityType,
  (_state: unknown, props: { entityId?: string }) => props.entityId,
  (_state: unknown, props: { parentId?: string }) => props.parentId,
  getReportCellValueUtil,
)({
  keySelector: (state, props) =>
    `${props.location}:${props.entityType}:${props.entityId}:${props.columnHeaderId}:${props.parentId}` || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 400 }),
});

export const getReportCellNumberValue = createCachedSelector(
  getReport,
  (_state: unknown, props: { columnHeaderId?: string }) => props.columnHeaderId,
  (_state: unknown, props: { entityType?: string }) => props.entityType,
  (_state: unknown, props: { entityId?: string }) => props.entityId,
  (_state: unknown, props: { parentId?: string }) => props.parentId,
  getReportCellNumberValueUtil,
)({
  keySelector: (state, props) =>
    `${props.location}:${props.entityType}:${props.entityId}:${props.columnHeaderId}:${props.parentId}` || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 400 }),
});

export const NOOP_BREADCRUMB_VALUES = REPORT_WRAPPER_ENTITY_IDS;

export const PIE_TOP_LEVEL_WRAPPER_ENTITY_IDS = REPORT_WRAPPER_ENTITY_IDS;

const hasRowNumericData = (row: ReportRow): boolean =>
  row.cellData?.some((cell: ReportCell) => !isNaN(Number(cell.value || 0))) || false;

const isValidPieRow = (row: ReportRow): boolean =>
  row.entityId !== 'TOTAL' && row.entityType !== 'TRANSACTION_TYPE' && hasRowNumericData(row);

export const getTopLevelPieRows = (groupedRows: Record<string, ReportRow[]>): ReportRow[] => {
  const rootRows = (groupedRows['-1'] ?? []).filter(isValidPieRow);

  if (!rootRows.length) {
    return [];
  }

  let currentRows = rootRows;

  while (currentRows.length > 0) {
    const wrapperRows = currentRows.filter((row) =>
      PIE_TOP_LEVEL_WRAPPER_ENTITY_IDS.has((row.entityId || '').toUpperCase()),
    );

    if (!wrapperRows.length) {
      return currentRows;
    }

    const nextRows = wrapperRows.flatMap((row) => (row.id ? (groupedRows[row.id] ?? []) : []).filter(isValidPieRow));

    if (!nextRows.length) {
      return currentRows;
    }

    currentRows = nextRows;
  }

  return rootRows;
};

export const isNoopBreadcrumb = (crumb: Breadcrumb): boolean => NOOP_BREADCRUMB_VALUES.has(crumb.categoryId || '');

type PieChartDrilldownProps = {
  location: string;
  breadcrumbs?: Breadcrumb[];
  includeEverythingElse?: boolean;
  showExcludedTxns?: boolean;
};
const EMPTY_BREADCRUMBS: Breadcrumb[] = [];
const EMPTY_EXCLUDED_TRANSACTION_IDS = new Set<string>();
const EMPTY_TRANSACTION_IDS: string[] = [];

const selectPieBreadcrumbs = (_state: unknown, props: PieChartDrilldownProps) =>
  props.breadcrumbs && props.breadcrumbs.length > 0 ? props.breadcrumbs : EMPTY_BREADCRUMBS;

const pieDrilldownSelectorOptions = {
  memoize: resettableLruMemoize,
  argsMemoize: resettableLruMemoize,
  memoizeOptions: {
    maxSize: 10,
    resultEqualityCheck: shallowEqual,
    equalityCheck: isEqual,
  },
};

const isEverythingElseBreadcrumb = (crumb: Breadcrumb): boolean =>
  crumb.categoryId === 'everything-else' || crumb.category === 'Everything else';

// Only TRANSACTION reports drive pie drill-downs. `report.reportType` alone is not sufficient:
// `transformResponseToReport` falls back to 'TRANSACTION' when QCS omits the field, so a comparison
// report can arrive claiming to be one and slip past this guard. Drilling into such a pie would
// merge transactions from two different date ranges into a single segment.
const isPieDrilldownReport = (report: Report | null | undefined): report is Report =>
  !!report && report.reportType === 'TRANSACTION' && !isComparisonReport(report);

/**
 * Walks the breadcrumbs from the top-level pie rows.
 * - `currentRows` are the rows the chart renders at the resolved level.
 * - `scopeRows` are the rows whose transactions the register lists: the selected row, the
 *   "Everything else" remainder, or every top-level row when nothing is selected. A breadcrumb that
 *   matches no row leaves the scope empty rather than widening it to the parent level.
 */
const navigatePieBreadcrumbs = (
  groupedRows: Record<string, ReportRow[]>,
  breadcrumbs: Breadcrumb[],
): { currentRows: ReportRow[]; scopeRows: ReportRow[] } => {
  let currentRows = getTopLevelPieRows(groupedRows);
  let scopeRows = currentRows;

  for (const crumb of breadcrumbs) {
    // "All categories" is a UI-only breadcrumb.
    if (crumb.categoryId === 'all-categories' || crumb.category === 'All categories') {
      continue;
    }

    if (isEverythingElseBreadcrumb(crumb)) {
      // For "Everything else", we want to show all remaining categories beyond the top 11
      const sortedRows = [...currentRows].sort((a: ReportRow, b: ReportRow) => {
        const aValue = Number(
          a.cellData?.find((cell: ReportCell) => cell.value && !isNaN(Number(cell.value)))?.value || 0,
        );
        const bValue = Number(
          b.cellData?.find((cell: ReportCell) => cell.value && !isNaN(Number(cell.value)))?.value || 0,
        );
        return Math.abs(bValue) - Math.abs(aValue);
      });

      currentRows = sortedRows.length > MAX_PIE_CHART_ITEMS ? sortedRows.slice(MAX_PIE_CHART_ITEMS) : [];
      scopeRows = currentRows;
      continue;
    }

    // Evaluate all rows and keep the highest-priority match:
    // 3 = id (validated against categoryId when present), 2 = categoryId, 1 = name fallback.
    // Row ids are positional and QCS renumbers them when the report regenerates, so a retained id
    // that now belongs to a different entity is stale and must not outrank the entity match.
    const targetRow = currentRows.reduce<{ row: ReportRow | undefined; priority: number }>(
      (best, row) => {
        let priority = 0;

        if (crumb.id && row.id === crumb.id && (!crumb.categoryId || row.entityId === crumb.categoryId)) {
          priority = 3;
        } else if (crumb.categoryId && row.entityId === crumb.categoryId) {
          priority = 2;
        } else if (!crumb.categoryId && row.name === crumb.name) {
          priority = 1;
        }

        // Keep first row on ties to preserve stable ordering.
        if (priority > best.priority) {
          return { row, priority };
        }

        return best;
      },
      { row: undefined, priority: 0 },
    ).row;

    if (targetRow && targetRow.id) {
      scopeRows = [targetRow];
      // Allow drill-down for any grouping type (category, payee, tag, account), but never transaction rows.
      const validChildren = (groupedRows[targetRow.id] ?? []).filter(
        (child) => child.entityId !== 'TOTAL' && child.entityType !== 'TRANSACTION_TYPE' && hasTransactionData(child),
      );
      // No valid children - show just this single category
      currentRows = validChildren.length > 0 ? validChildren : [targetRow];
    } else if (isNoopBreadcrumb(crumb)) {
      // Wrapper entity (e.g. BUSINESS/PERSONAL group) that getTopLevelPieRows already
      // walked past — skip it and continue navigating with the remaining breadcrumbs.
      continue;
    } else {
      scopeRows = [];
      break;
    }
  }

  return { currentRows, scopeRows };
};

export const getPieChartDataWithDrillDown = createSelector(
  [
    getReport,
    rowsByParentId,
    selectPieBreadcrumbs,
    entitlementsSelectors.getBusinessEnabled,
    (_state: unknown, props: PieChartDrilldownProps) => props.includeEverythingElse ?? false,
    (_state: unknown, props: PieChartDrilldownProps) => props.showExcludedTxns,
  ],
  (
    report,
    groupedRows,
    breadcrumbs,
    businessEnabled,
    includeEverythingElse,
    showExcludedTxns,
  ): PieChartDataResult | null => {
    if (!isPieDrilldownReport(report)) {
      return null;
    }

    const shouldIncludeEverythingElse = includeEverythingElse && !breadcrumbs.some(isEverythingElseBreadcrumb);
    const { currentRows } = navigatePieBreadcrumbs(groupedRows, breadcrumbs);

    return transformRowsToPieChartData(currentRows, report, breadcrumbs, businessEnabled, groupedRows, {
      includeEverythingElse: shouldIncludeEverythingElse,
      showExcludedTxns,
    });
  },
  pieDrilldownSelectorOptions,
);

/**
 * Transaction segment ids (split item id, else transaction id) under the pie drill-down selected by
 * `breadcrumbs`, resolved with the same row-id-first navigation as `getPieChartDataWithDrillDown`.
 * Unlike the chart's `transactionsBySegment`, this is not limited to visible slices: it includes
 * nested and zero-value rows so the register lists every transaction in the selected branch.
 */
export const getPieDrilldownTransactionIds = createSelector(
  [getReport, rowsByParentId, selectPieBreadcrumbs],
  (report, groupedRows, breadcrumbs): string[] => {
    if (!isPieDrilldownReport(report)) {
      return EMPTY_TRANSACTION_IDS;
    }

    const { scopeRows } = navigatePieBreadcrumbs(groupedRows, breadcrumbs);
    if (!scopeRows.length) {
      return EMPTY_TRANSACTION_IDS;
    }

    return Array.from(new Set(scopeRows.flatMap((row) => collectTransactionSegmentIdsFromRow(row, groupedRows))));
  },
  pieDrilldownSelectorOptions,
);

// Helper function to check if a row has transaction data (including zero amounts)
const hasTransactionData = (row: ReportRow): boolean => {
  return (
    row.cellData?.some((cell: ReportCell) => {
      const value = Number(cell.value || 0);
      return !isNaN(value); // Show all numeric values including zero
    }) || false
  );
};

// Helper function to transform rows to pie chart data
const transformRowsToPieChartData = (
  rows: ReportRow[],
  report: Report,
  breadcrumbs?: Breadcrumb[],
  businessEnabled?: boolean,
  groupedRows?: Record<string, ReportRow[]>,
  options?: {
    includeEverythingElse?: boolean;
    showExcludedTxns?: boolean;
  },
): PieChartDataResult | null => {
  if (!rows.length) {
    return null;
  }

  const includeEverythingElse = options?.includeEverythingElse ?? false;

  const cols = report?.columns ?? [];

  // Group rows by category and sum their totals to prevent duplicates
  const categoryMap = new Map<string, PieChartDataAccumulator>();
  // Collect transaction IDs by pie segment (category)
  const transactionsBySegment: Record<string, string[]> = {};

  const negator = report?.reportSubType === 'INCOME' ? 1 : -1;
  const showExcludedTxns =
    options?.showExcludedTxns ?? Boolean(report?.configuration?.displayOptions?.showExcludedTxns);

  // Note: Removed complex drill-down aggregation logic - we'll handle parent aggregation in the component

  rows.forEach((row: ReportRow) => {
    // Pie chart slices should always represent grouped rows, never individual transactions or usage wrappers.
    if (row.entityType === 'TRANSACTION_TYPE' || row.entityType === 'USAGE_TYPE') {
      return;
    }

    if (
      shouldHideExcludedZeroTotalReportRow({
        row,
        excludedTransactionIds: EMPTY_EXCLUDED_TRANSACTION_IDS,
        rowsByParentId: groupedRows,
        showExcludedTxns,
      })
    ) {
      return;
    }

    // Find the cell with the actual amount value
    const valueCell = row.cellData?.find((cell: ReportCell) => cell.value && !isNaN(Number(cell.value)));
    // Rows where all transactions are excluded have no value in the amount cell but still
    // have childrenEntityIds — include them with total=0 so they appear in the legend.
    if (!valueCell && !row.childrenEntityIds?.length) {
      return;
    }

    const total = valueCell ? Number(valueCell.value) : 0;

    // Show all transactions - remove value threshold filtering
    // Note: Keeping transactions with zero values now to show all data

    // Show all transactions regardless of positive/negative for complete data
    // Note: Previously filtered positive values for expense reports, now showing all

    // Determine category name based on available data
    let category = 'Unknown';

    // For transaction types, use category information from breadcrumbs or row data
    if (row.entityType === 'TRANSACTION_TYPE') {
      // When we have breadcrumbs, use the category context from the last breadcrumb
      if (breadcrumbs && breadcrumbs.length > 1) {
        const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
        if (currentBreadcrumb && currentBreadcrumb.category) {
          category = currentBreadcrumb.category;
        } else if (currentBreadcrumb && currentBreadcrumb.name) {
          category = currentBreadcrumb.name;
        } else {
          // Fallback to row data
          category = row.name && row.name !== 'undefined' ? row.name : 'Unknown';
        }
      } else {
        // No breadcrumb context, use row data
        category = row.name && row.name !== 'undefined' ? row.name : 'Unknown';
      }
    } else if (row.entityId) {
      // Handle special uncategorized cases for business users first
      if (businessEnabled && row.entityId === 'UNCATEGORIZED_BUSINESS') {
        category = 'Uncategorized (Business)';
      } else if (businessEnabled && row.entityId === 'UNCATEGORIZED_PERSONAL') {
        category = 'Uncategorized (Personal)';
      } else {
        // Check if we have a name to use
        if (row.name && row.name !== 'undefined') {
          category = row.name;
        } else {
          // Handle different entity types appropriately
          const column = cols[0];
          if (column?.headerType === 'QCS_ENTITY') {
            if (column.headerValue.qcs_entity === 'ACCOUNT') {
              category = accountsUtils.getAccountString(row.entityId) || row.entityId;
            } else {
              category = row.name || 'Unknown';
            }
          } else {
            category = row.name || 'Unknown';
          }
        }
      }
    } else if (row.name && row.name !== 'undefined') {
      category = row.name;
    }

    // For top-level categories, use just the category name
    // For transaction types, use unique identifier to differentiate individual transactions
    let categoryKey = `${category}-${row.id}`; // Default to just category name

    if (row.entityType === 'TRANSACTION_TYPE') {
      // For individual transactions, use unique identifier
      categoryKey = `${category}_${row.entityId || 'unknown'}`;
    } else if (breadcrumbs && breadcrumbs.length > 1) {
      // For drill-down scenarios with category types, use category name
      categoryKey = category;
    }

    const flippedTotal = total * negator; // Multiply by -1 to flip sign

    const segmentIds = collectTransactionSegmentIdsFromRow(row, groupedRows);
    if (segmentIds.length) {
      const segmentSet = new Set(transactionsBySegment[category] ?? []);
      segmentIds.forEach((segmentId) => segmentSet.add(segmentId));
      transactionsBySegment[category] = Array.from(segmentSet);
    }

    if (categoryMap.has(categoryKey)) {
      // Add to existing category
      const existing = categoryMap.get(categoryKey)!;
      existing.signedTotal += flippedTotal;
      existing.transactionIds = Array.from(new Set([...existing.transactionIds, ...segmentIds]));
    } else {
      // Create new category entry
      const item: PieChartDataAccumulator = {
        category,
        categoryId: row.entityId || 'unknown',
        groupKey: row.id || row.entityId || 'unknown',
        // Include original row data for drill-down functionality
        originalRow: row,
        transactionIds: segmentIds,
        signedTotal: flippedTotal,
      };
      categoryMap.set(categoryKey, item);
    }
  });

  const pieChartData = Array.from(categoryMap.values())
    .map((item): PieChartDataItem => {
      const { total, displayTotal, showSign } = getPieChartDisplay(report?.reportSubType, item.signedTotal);

      return {
        category: item.category,
        total,
        displayTotal,
        categoryId: item.categoryId,
        groupKey: item.groupKey,
        originalRow: item.originalRow,
        transactionIds: item.transactionIds,
        showSign,
      };
    })
    .sort((a: PieChartDataItem, b: PieChartDataItem) => b.total - a.total)
    .filter((item) => shouldRenderPieChartSlice(item.total) || (item.originalRow.childrenEntityIds?.length ?? 0) > 0);

  // Order the fields array to match the sorted pie chart data
  const orderedFields = pieChartData.map((item: PieChartDataItem) => item.category);

  const transactionsBySegmentForChart = pruneTransactionsBySegmentToPieCategories(transactionsBySegment, pieChartData);

  if (includeEverythingElse && pieChartData.length > MAX_PIE_CHART_ITEMS) {
    const topItems = pieChartData.slice(0, MAX_PIE_CHART_ITEMS);
    const remainingItems = pieChartData.slice(MAX_PIE_CHART_ITEMS);
    const everythingElseTotal = remainingItems.reduce((sum, item) => sum + item.total, 0);
    const everythingElseTxnIds = Array.from(new Set(remainingItems.flatMap((item) => item.transactionIds)));

    const everythingElseRow: ReportRow = {
      id: 'everything-else',
      name: 'Everything else',
      entityId: 'everything-else',
      entityType: 'EVERYTHING_ELSE',
      childrenEntityIds: everythingElseTxnIds,
      cellData: [],
      other: true,
    };

    const everythingElseItem: PieChartDataItem = {
      category: 'Everything else',
      total: everythingElseTotal,
      categoryId: 'everything-else',
      groupKey: 'everything-else',
      originalRow: everythingElseRow,
      transactionIds: everythingElseTxnIds,
      isEverythingElse: true,
    };

    const dataWithEverythingElse = [...topItems, everythingElseItem];

    return {
      data: dataWithEverythingElse,
      orderedFields: dataWithEverythingElse.map((item) => item.category),
      transactionsBySegment: {
        ...transactionsBySegmentForChart,
        ['Everything else']: everythingElseTxnIds,
      },
    };
  }

  return {
    data: pieChartData,
    orderedFields,
    transactionsBySegment: transactionsBySegmentForChart,
  };
};

/**
 * Selector to get multiline chart data from a report for NET_WORTH reports
 * Returns data formatted for the MultilineChart component (previously AreaChart)
 */
export interface ChartDataPoint {
  category: string;
  custom: string;
  startOn: string;
  endOn: string;
  total?: number;
  [accountName: string]: string | number | undefined;
}

type MultilineChartData = {
  data: ChartDataPoint[];
  orderedFields: string[];
  fieldExpectedSigns: Record<string, ReportRow['expectedSign']>;
  // Display label for each field key — a display name (row.name) can collide across two
  // unrelated accounts (e.g. a mortgage loan named after the same address as its property
  // asset), so field keys must be a stable per-row identifier and names are looked up here.
  fieldNames: Record<string, string>;
};

const defaultChartData: MultilineChartData = {
  data: [],
  orderedFields: [],
  fieldExpectedSigns: {},
  fieldNames: {},
};

export type AccountColorFamily = 'ASSET' | 'LIABILITY';

export interface StackedAreaChartData {
  chartData: ChartDataPoint[];
  orderedFields: string[];
  tooltipFields: string[];
  splitSignsStacked: boolean[];
  seriesNames: Record<string, string>;
  assetFields: string[];
  liabilityFields: string[];
  fieldColorFamilies: Record<string, AccountColorFamily>;
  totalField?: string;
  yAxisExtents?: { min: number; max: number };
}

interface StackedAreaFieldEntry {
  field: string;
  endingValue: number;
  order: number;
}

const SYNTHETIC_FIELD_PREFIX = '__simplifi_net_worth_split_sign__';
const POSITIVE_FIELD_PREFIX = `${SYNTHETIC_FIELD_PREFIX}positive__`;
const NEGATIVE_FIELD_PREFIX = `${SYNTHETIC_FIELD_PREFIX}negative__`;
const NET_WORTH_FIELD = `${SYNTHETIC_FIELD_PREFIX}net_worth`;
const TOTAL_ASSETS_FIELD = `${SYNTHETIC_FIELD_PREFIX}total_assets`;
const TOTAL_LIABILITIES_FIELD = `${SYNTHETIC_FIELD_PREFIX}total_liabilities`;
const ACCOUNT_GROUP_FIELD_PREFIX = '__simplifi_net_worth_account_group__';
const NICE_AXIS_STEPS = [1, 2, 2.5, 3, 4, 5, 6, 8, 10];
const TARGET_Y_AXIS_TICK_COUNT = 6;

const defaultStackedAreaChartData: StackedAreaChartData = {
  chartData: [],
  orderedFields: [],
  tooltipFields: [],
  splitSignsStacked: [],
  seriesNames: {},
  assetFields: [],
  liabilityFields: [],
  fieldColorFamilies: {},
  totalField: undefined,
  yAxisExtents: undefined,
};

const getPositiveField = (field: string): string => `${POSITIVE_FIELD_PREFIX}${field}`;
const getNegativeField = (field: string): string => `${NEGATIVE_FIELD_PREFIX}${field}`;

const getAccountColorFamily = (
  expectedSign: ReportRow['expectedSign'] | undefined,
  hasPositiveValue: boolean,
  hasNegativeValue: boolean,
): AccountColorFamily => {
  if (expectedSign === 'NEGATIVE') {
    return 'LIABILITY';
  }

  if (expectedSign === 'POSITIVE') {
    return 'ASSET';
  }

  return hasNegativeValue && !hasPositiveValue ? 'LIABILITY' : 'ASSET';
};

const getNiceAxisInterval = (range: number): number => {
  const rawInterval = range / TARGET_Y_AXIS_TICK_COUNT;
  const magnitude = 10 ** Math.floor(Math.log10(rawInterval));
  const normalizedInterval = rawInterval / magnitude;
  const niceStep = NICE_AXIS_STEPS.find((step) => normalizedInterval <= step) ?? 10;

  return niceStep * magnitude;
};

const getStackedAreaTotal = (point: ChartDataPoint, activeFields: string[], totalField?: string): number => {
  if (!totalField) {
    return 0;
  }

  if (typeof point.total === 'number') {
    return point.total;
  }

  return activeFields.reduce((sum, field) => {
    const value = point[field];
    return typeof value === 'number' ? sum + value : sum;
  }, 0);
};

const getYAxisExtents = (min: number, max: number): { min: number; max: number } | undefined =>
  min === max
    ? undefined
    : (() => {
        const interval = getNiceAxisInterval(max - min);

        return {
          min: Math.floor(min / interval) * interval,
          max: Math.ceil(max / interval) * interval,
        };
      })();

const getRowValue = (row: ReportRow | undefined, columnIndex: number): number => {
  const value = Number(row?.cellData[columnIndex]?.value ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const getAccountGroupChartData = (
  report: Report | null | undefined,
  timestep: Timestep,
  addTotal: boolean | undefined,
): MultilineChartData => {
  if (report?.reportSubType !== 'NET_WORTH' || !report.columns?.length || !report.rows?.length) {
    return defaultChartData;
  }

  const rowsByParentId = new Map<string, ReportRow[]>();
  report.rows.forEach((row) => {
    if (!row.parentId) return;
    const siblings = rowsByParentId.get(row.parentId) ?? [];
    siblings.push(row);
    rowsByParentId.set(row.parentId, siblings);
  });

  const getDescendantAccountRows = (groupRow: ReportRow): ReportRow[] => {
    if (!groupRow.id) return [];

    const accountRows: ReportRow[] = [];
    const pendingRows = [...(rowsByParentId.get(groupRow.id) ?? [])];
    const visitedRows = new Set<ReportRow>();

    while (pendingRows.length) {
      const row = pendingRows.pop();
      if (!row || visitedRows.has(row)) continue;
      visitedRows.add(row);

      if (row.entityType === 'ACCOUNT_TYPE') {
        if (!row.isExcludedFromReports && row.cellData?.length) {
          accountRows.push(row);
        }
        continue;
      }

      if (row.id) {
        pendingRows.push(...(rowsByParentId.get(row.id) ?? []));
      }
    }

    return accountRows;
  };

  const groupFields = (['ASSETS', 'LIABILITIES'] as const).flatMap((entityId) => {
    const topLevelRow = report.rows.find((row) => row.entityId === entityId);
    if (!topLevelRow?.id) return [];

    return (rowsByParentId.get(topLevelRow.id) ?? []).flatMap((groupRow, index) => {
      const accountRows = getDescendantAccountRows(groupRow);
      const name = groupRow.name ?? groupRow.entityId;
      const hasChildren = Boolean(groupRow.id && rowsByParentId.get(groupRow.id)?.length);
      if (!hasChildren || !name) return [];

      return [
        {
          accountRows,
          expectedSign: entityId === 'ASSETS' ? ('POSITIVE' as const) : ('NEGATIVE' as const),
          field: `${ACCOUNT_GROUP_FIELD_PREFIX}${entityId.toLowerCase()}__${groupRow.id ?? groupRow.entityId ?? index}`,
          groupRow,
          name,
        },
      ];
    });
  });

  if (!groupFields.length) {
    return defaultChartData;
  }

  const netWorthRow = report.rows.find(
    (row) => row.parentId === '-1' && (row.entityId === 'TOTAL' || row.name?.trim().toUpperCase() === 'NET WORTH'),
  );
  const orderedFields = groupFields.map(({ field }) => field);
  const fieldExpectedSigns = Object.fromEntries(groupFields.map(({ expectedSign, field }) => [field, expectedSign]));
  const fieldNames = Object.fromEntries(groupFields.map(({ field, name }) => [field, name]));
  const data = report.columns.reduce<ChartDataPoint[]>((chartData, column, columnIndex) => {
    if (column.headerType !== 'DATE_RANGE') return chartData;

    const category = formatColumnDateLabel(
      report.columns,
      timestep,
      column.headerValue.startOn,
      column.headerValue.endOn,
    );
    const values = Object.fromEntries(
      groupFields.map(({ accountRows, field, groupRow }) => {
        const rawGroupValue = groupRow.cellData[columnIndex]?.value;
        const numericGroupValue = rawGroupValue === null || rawGroupValue === undefined ? NaN : Number(rawGroupValue);
        const value = Number.isFinite(numericGroupValue)
          ? numericGroupValue
          : accountRows.reduce((total, row) => total + getRowValue(row, columnIndex), 0);

        return [field, value];
      }),
    );
    const groupedTotal = Object.values(values).reduce((total, value) => total + value, 0);

    chartData.push({
      category,
      custom: category,
      startOn: column.headerValue.startOn,
      endOn: column.headerValue.endOn,
      ...values,
      total: addTotal ? (netWorthRow ? getRowValue(netWorthRow, columnIndex) : groupedTotal) : undefined,
    });

    return chartData;
  }, []);

  return { data, orderedFields, fieldExpectedSigns, fieldNames };
};

const getAssetsVsLiabilitiesChartData = (
  report: Report | null | undefined,
  timestep: Timestep,
  addTotal: boolean | undefined,
): StackedAreaChartData => {
  if (report?.reportSubType !== 'NET_WORTH' || !report.columns?.length || !report.rows?.length) {
    return defaultStackedAreaChartData;
  }

  const assetsRow = report.rows.find((row) => row.entityId === 'ASSETS');
  const liabilitiesRow = report.rows.find((row) => row.entityId === 'LIABILITIES');
  const netWorthRow = report.rows.find(
    (row) => row.parentId === '-1' && (row.entityId === 'TOTAL' || row.name?.trim().toUpperCase() === 'NET WORTH'),
  );

  if (!assetsRow && !liabilitiesRow) {
    return defaultStackedAreaChartData;
  }

  let yAxisMin = 0;
  let yAxisMax = 0;
  const chartData = report.columns.reduce<ChartDataPoint[]>((data, column, columnIndex) => {
    if (column.headerType !== 'DATE_RANGE') {
      return data;
    }

    const assets = getRowValue(assetsRow, columnIndex);
    const liabilities = getRowValue(liabilitiesRow, columnIndex);
    const netWorth = netWorthRow ? getRowValue(netWorthRow, columnIndex) : assets + liabilities;
    const category = formatColumnDateLabel(
      report.columns,
      timestep,
      column.headerValue.startOn,
      column.headerValue.endOn,
    );

    data.push({
      category,
      custom: category,
      startOn: column.headerValue.startOn,
      endOn: column.headerValue.endOn,
      [TOTAL_ASSETS_FIELD]: assets,
      [TOTAL_LIABILITIES_FIELD]: liabilities,
      ...(addTotal ? { [NET_WORTH_FIELD]: netWorth } : {}),
    });

    yAxisMin = Math.min(yAxisMin, assets, liabilities, addTotal ? netWorth : 0);
    yAxisMax = Math.max(yAxisMax, assets, liabilities, addTotal ? netWorth : 0);
    return data;
  }, []);

  if (!chartData.length) {
    return defaultStackedAreaChartData;
  }

  const totalField = addTotal ? NET_WORTH_FIELD : undefined;
  const orderedFields = totalField
    ? [TOTAL_ASSETS_FIELD, TOTAL_LIABILITIES_FIELD, totalField]
    : [TOTAL_ASSETS_FIELD, TOTAL_LIABILITIES_FIELD];

  return {
    chartData,
    orderedFields,
    tooltipFields: [TOTAL_ASSETS_FIELD, TOTAL_LIABILITIES_FIELD],
    splitSignsStacked: orderedFields.map(() => false),
    seriesNames: {
      [TOTAL_ASSETS_FIELD]: 'Assets',
      [TOTAL_LIABILITIES_FIELD]: 'Liabilities',
      ...(totalField ? { [totalField]: 'Net Worth' } : {}),
    },
    assetFields: [TOTAL_ASSETS_FIELD],
    liabilityFields: [TOTAL_LIABILITIES_FIELD],
    fieldColorFamilies: {
      [TOTAL_ASSETS_FIELD]: 'ASSET',
      [TOTAL_LIABILITIES_FIELD]: 'LIABILITY',
    },
    totalField,
    yAxisExtents: getYAxisExtents(yAxisMin, yAxisMax),
  };
};

export const getMultilineChartDataFromReport = createSelector(
  getReport,
  (_state: unknown, props: { timestep?: Timestep }) => props.timestep || 'MONTH',
  (_state: unknown, props: { addTotal?: boolean }) => props.addTotal,
  (report, timestep, addTotal) => {
    // Only support NET_WORTH and SAVINGS reports - return null for others to avoid unnecessary calculation
    if (report?.reportSubType !== 'NET_WORTH' && report?.reportSubType !== 'SAVINGS') {
      return defaultChartData;
    }

    if (!report?.columns || !report?.rows) {
      return defaultChartData;
    }

    const cols = report.columns;
    const rows = report.rows;
    const rowsById = new Map(rows.map((row) => [row.id, row]));

    const getAccountExpectedSign = (row: ReportRow): ReportRow['expectedSign'] => {
      let currentRow: ReportRow | undefined = row;

      while (currentRow?.parentId && currentRow.parentId !== '-1') {
        currentRow = rowsById.get(currentRow.parentId);
        if (currentRow?.entityId === 'ASSETS') {
          return 'POSITIVE';
        }
        if (currentRow?.entityId === 'LIABILITIES') {
          return 'NEGATIVE';
        }
      }

      return row.expectedSign;
    };

    // Helper to get account display name
    const getAccountName = (row: ReportRow) => {
      if (row.entityId && row.entityType === 'ACCOUNT') {
        return accountsUtils.getAccountString(row.entityId) || row.name || row.entityId;
      }
      return row.name;
    };

    // Single reduce to filter accounts and build ordered fields
    const orderedFieldsSet = new Set<string>();
    const fieldExpectedSigns: Record<string, ReportRow['expectedSign']> = {};
    const fieldNames: Record<string, string> = {};
    const accountRows: ReportRow[] = [];

    rows.forEach((row) => {
      // Filter logic: only include valid account rows
      if (row.entityType === 'ACCOUNT_TYPE' && row.cellData && row.cellData.length > 0 && !row.isExcludedFromReports) {
        const accountName = getAccountName(row);
        const fieldKey = getReportEntityKey(row);
        if (accountName && accountName !== 'undefined' && fieldKey) {
          accountRows.push(row);
          orderedFieldsSet.add(fieldKey);
          fieldExpectedSigns[fieldKey] = getAccountExpectedSign(row);
          fieldNames[fieldKey] = accountName;
        }
      }
    });

    const orderedFields = Array.from(orderedFieldsSet);

    if (accountRows.length === 0) {
      return defaultChartData;
    }

    // Build chart data for each time period (column)
    const chartData = cols.reduce<ChartDataPoint[]>((data, column, columnIndex) => {
      if (column.headerType === 'DATE_RANGE') {
        const category = formatColumnDateLabel(cols, timestep, column.headerValue.startOn, column.headerValue.endOn);

        // Build data point with all account values for this time period
        const dataPoint = accountRows.reduce<ChartDataPoint>(
          (point, row) => {
            if (row.isExcludedFromReports) {
              return point;
            }
            const accountName = getAccountName(row);
            const fieldKey = getReportEntityKey(row);
            if (accountName && accountName !== 'undefined' && fieldKey) {
              const cellValue = row.cellData?.[columnIndex]?.value;
              const val = cellValue ? Number(cellValue) : 0;
              point[fieldKey] = val;
              if (addTotal) {
                point.total = (point.total ?? 0) + val;
              }
            }
            return point;
          },
          {
            category,
            custom: category, // For custom tooltip text
            startOn: column.headerValue.startOn,
            endOn: column.headerValue.endOn,
            total: undefined,
          },
        );

        data.push(dataPoint);
      }
      return data;
    }, []);

    return {
      data: chartData,
      orderedFields,
      fieldExpectedSigns,
      fieldNames,
    };
  },
);

type StackedAreaChartSelectorProps = {
  location: string;
  timestep?: Timestep;
  addTotal?: boolean;
  isSavingsReport?: boolean;
  chartGrouping?: ChartView;
};

const getIndividualAccountChartDataForStackedArea = (
  state: Pick<RootState, typeof GENERATED_REPORTS_REDUCER_KEY>,
  props: StackedAreaChartSelectorProps,
): MultilineChartData =>
  !props.chartGrouping || props.chartGrouping === 'INDIVIDUAL_ACCOUNTS'
    ? getMultilineChartDataFromReport(state, props)
    : defaultChartData;

export const getStackedAreaChartDataFromReport = createSelector(
  getIndividualAccountChartDataForStackedArea,
  getReport,
  (_state: unknown, props: StackedAreaChartSelectorProps) => props.timestep || 'MONTH',
  (_state: unknown, props: StackedAreaChartSelectorProps) => props.addTotal,
  (_state: unknown, props: StackedAreaChartSelectorProps) => props.isSavingsReport,
  (_state: unknown, props: StackedAreaChartSelectorProps) => props.chartGrouping ?? 'INDIVIDUAL_ACCOUNTS',
  (multilineChartData, report, timestep, addTotal, isSavingsReport, chartGrouping): StackedAreaChartData => {
    if (chartGrouping === 'ASSETS_VS_LIABILITIES') {
      return getAssetsVsLiabilitiesChartData(report, timestep, addTotal);
    }

    const sourceChartData =
      chartGrouping === 'ACCOUNT_GROUPS' ? getAccountGroupChartData(report, timestep, addTotal) : multilineChartData;
    const fields = sourceChartData.orderedFields;
    const data = sourceChartData.data;
    const fieldExpectedSigns = sourceChartData.fieldExpectedSigns;

    if (!data.length) {
      return defaultStackedAreaChartData;
    }

    const activeFields = fields.filter((field) =>
      data.some((point) => typeof point[field] === 'number' && point[field] !== 0),
    );
    const endingDataPoint = data[data.length - 1];
    const positiveFieldEntries: StackedAreaFieldEntry[] = [];
    const negativeFieldEntries: StackedAreaFieldEntry[] = [];
    const fieldColorFamilies: Record<string, AccountColorFamily> = {};
    const seriesNames: Record<string, string> = {};

    activeFields.forEach((field, order) => {
      const fieldName = sourceChartData.fieldNames[field] ?? field;
      const hasPositiveValue = data.some((point) => {
        const value = point[field];
        return typeof value === 'number' && value > 0;
      });
      const hasNegativeValue = data.some((point) => {
        const value = point[field];
        return typeof value === 'number' && value < 0;
      });
      const endingValue = endingDataPoint?.[field];
      const numericEndingValue = typeof endingValue === 'number' ? endingValue : 0;
      const colorFamily = getAccountColorFamily(fieldExpectedSigns[field], hasPositiveValue, hasNegativeValue);

      if (isSavingsReport && colorFamily === 'LIABILITY') {
        return;
      }

      if (hasPositiveValue) {
        const positiveField = getPositiveField(field);
        positiveFieldEntries.push({
          field: positiveField,
          endingValue: numericEndingValue > 0 ? numericEndingValue : 0,
          order,
        });
        fieldColorFamilies[positiveField] = colorFamily;
        seriesNames[positiveField] = hasNegativeValue ? `${fieldName} (positive)` : fieldName;
      }

      if (hasNegativeValue) {
        const negativeField = getNegativeField(field);
        negativeFieldEntries.push({
          field: negativeField,
          endingValue: numericEndingValue < 0 ? Math.abs(numericEndingValue) : 0,
          order,
        });
        fieldColorFamilies[negativeField] = colorFamily;
        seriesNames[negativeField] = hasPositiveValue ? `${fieldName} (negative)` : fieldName;
      }
    });

    const positiveFields = positiveFieldEntries
      .sort((a, b) => b.endingValue - a.endingValue || a.order - b.order)
      .map(({ field }) => field);
    const negativeFields = negativeFieldEntries
      .sort((a, b) => b.endingValue - a.endingValue || a.order - b.order)
      .map(({ field }) => field);

    const stackedFields = [...positiveFields, ...negativeFields];
    const displayFields = [...positiveFields, ...negativeFields];
    const assetFields = displayFields.filter((field) => fieldColorFamilies[field] !== 'LIABILITY');
    const liabilityFields = displayFields.filter((field) => fieldColorFamilies[field] === 'LIABILITY');
    const tooltipFields = [...assetFields, ...liabilityFields];
    const positiveFieldSet = new Set(positiveFields);
    const negativeFieldSet = new Set(negativeFields);
    const totalField = addTotal ? NET_WORTH_FIELD : undefined;

    if (totalField) {
      seriesNames[totalField] = 'Net Worth';
    }

    let yAxisMin = 0;
    let yAxisMax = 0;
    const chartData = data.map((point) => {
      let positiveStack = 0;
      let negativeStack = 0;
      const total = getStackedAreaTotal(point, activeFields, totalField);

      const chartDataPoint = {
        ...point,
        ...activeFields.reduce<Record<string, number>>((seriesPoint, field) => {
          const value = point[field];
          const positiveField = getPositiveField(field);
          const negativeField = getNegativeField(field);

          if (positiveFieldSet.has(positiveField)) {
            const positiveValue = typeof value === 'number' && value > 0 ? value : 0;
            seriesPoint[positiveField] = positiveValue;
            positiveStack += positiveValue;
          }

          if (negativeFieldSet.has(negativeField)) {
            const negativeValue = typeof value === 'number' && value < 0 ? value : 0;
            seriesPoint[negativeField] = negativeValue;
            negativeStack += negativeValue;
          }

          return seriesPoint;
        }, {}),
        ...(totalField ? { [totalField]: total } : {}),
      };

      yAxisMin = Math.min(yAxisMin, negativeStack, totalField ? total : 0);
      yAxisMax = Math.max(yAxisMax, positiveStack, totalField ? total : 0);

      return chartDataPoint;
    });
    const splitSignsStacked = stackedFields.map((_, index) => index !== 0 && index !== positiveFields.length);

    return {
      chartData,
      orderedFields: totalField ? [...stackedFields, totalField] : stackedFields,
      tooltipFields,
      splitSignsStacked: totalField ? [...splitSignsStacked, false] : splitSignsStacked,
      seriesNames,
      assetFields,
      liabilityFields,
      fieldColorFamilies,
      totalField,
      yAxisExtents: getYAxisExtents(yAxisMin, yAxisMax),
    };
  },
);

// AI Generated sortin algorithm
const sortLeafChildren = (children: NetWorthRow[]): NetWorthRow[] => {
  if (children.length === 0 || children.some((child) => child.children && child.children.length > 0)) {
    return children;
  }
  const rank = (child: NetWorthRow): number => {
    if (child.isExcludedFromReports) return 2;
    if (child.isClosed) return 1;
    return 0;
  };
  return [...children].sort((a, b) => {
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    const nameA = a.name ?? '';
    const nameB = b.name ?? '';
    return nameA.localeCompare(nameB);
  });
};

// Partially made via AI then edited with further adjustments and typing
export const getNetWorthNodeTree = createSelector(
  (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>, locationData: { location: string }) =>
    rowsByParentId(state, locationData),
  accountsSelectors.getAccountsById,
  (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>, locationData: { location: string }) =>
    getReport(state, locationData),
  (rowsByParentId, accountsById, report) => {
    function buildNodesTree(
      parentId: string | number = -1, // assume -1 is your top level
    ) {
      const key = String(parentId);
      const rows = rowsByParentId[key] ?? [];

      return rows.map((row: ReportRow) => {
        const cells = row.cellData ?? [];

        const earliestValue = cells.length > 0 ? Number(cells[0].startValue || 0) : null;

        const latestValue = cells.length > 0 ? Number(cells[cells.length - 1].value ?? 0) : null;

        const children = buildNodesTree(row.id);

        let isClosed;
        if (children.length === 0 && row.entityId) {
          isClosed = accountsById.get(row.entityId)?.isClosed;
        }

        let isExcludedFromReports;

        // How many direct children are included (Only one level deep)
        let includedCount = 0;
        let excludedCount = 0;

        if (children.length === 0) {
          isExcludedFromReports = row?.isExcludedFromReports ?? false;
          includedCount = 0;
          excludedCount = 0;
        } else {
          children?.forEach((myAccount: NetWorthRow) => {
            if (myAccount?.isExcludedFromReports === true) {
              // We want undefined values to not be true or false
              excludedCount += 1;
            } else if (myAccount?.isExcludedFromReports === false) {
              includedCount += 1;
            }
          });
        }

        // only include value if we have explicit string
        const deltaAmount = row.deltaAmount != null ? Number(row.deltaAmount) || 0 : undefined;
        const deltaPercentage = row.deltaPercentage != null ? Number(row.deltaPercentage) || 0 : undefined;

        const base: NetWorthRow = {
          id: row?.id,
          name: row?.name,
          isClosed: isClosed,
          entityId: row?.entityId,
          isTopLevel: parentId === -1,
          earliestValue,
          latestValue,
          deltaAmount,
          deltaPercentage,
          isExcludedFromReports,
          includedCount,
          excludedCount,
          children: [],
        };

        // Only attach children if there are any
        if (children.length > 0) {
          base.children = sortLeafChildren(children);
        }

        return base;
      });
    }

    // A comparison report has no asset/liability hierarchy and its cells are not balances, so
    // reading `cells[0].startValue` / `cells.at(-1).value` off them would invent numbers. Fall
    // through to the synthetic zero nodes below — consumers do `.find(n => n.entityId === 'ASSETS')`
    // and would break on a bare empty array.
    const topLevelNodes: NetWorthRow[] = isComparisonReport(report) ? [] : buildNodesTree(-1);

    const existingAssets = topLevelNodes.find((node) => node.entityId === 'ASSETS');
    const existingLiabilities = topLevelNodes.find((node) => node.entityId === 'LIABILITIES');

    // Synthetic fallback nodes (0 values, no children)
    const syntheticAssets: NetWorthRow = {
      id: '__synthetic_assets__',
      name: 'Assets',
      entityId: 'ASSETS',
      isTopLevel: true,
      earliestValue: 0,
      latestValue: 0,
      isExcludedFromReports: undefined,
      includedCount: 0,
      excludedCount: 0,
      children: [],
    };

    const syntheticLiabilities: NetWorthRow = {
      id: '__synthetic_liabilities__',
      name: 'Liabilities',
      entityId: 'LIABILITIES',
      isTopLevel: true,
      earliestValue: 0,
      latestValue: 0,
      isExcludedFromReports: undefined,
      includedCount: 0,
      excludedCount: 0,
      children: [],
    };

    const assetsNode = existingAssets ?? syntheticAssets;
    const liabilitiesNode = existingLiabilities ?? syntheticLiabilities;

    return [assetsNode, liabilitiesNode];
  },
);

export const getNetWorthNodeTreeIncludedIds = createSelector(getNetWorthNodeTree, (netWorthNodeTree) => {
  const accounts: string[] = [];
  const getAccountNodes = (node: NetWorthRow) => {
    if ((!node.children || node.children.length === 0) && !node.isExcludedFromReports && !node.isTopLevel) {
      if (node?.entityId) {
        accounts.push(node.entityId);
      }
      return;
    }
    if (node?.children) {
      node.children?.forEach(getAccountNodes);
    }
  };

  netWorthNodeTree.forEach(getAccountNodes);
  return accounts;
});

export const getNetWorthNode = createSelector(getReport, (report) => {
  const netWorthNode = report?.rows?.find((node) => node.parentId === '-1' && node.name === 'Net Worth') ?? undefined;

  return netWorthNode;
});

export const getRowCellDataItems = createSelector(
  getReport,
  (_state: unknown, props: { rowId?: string }) => props.rowId,
  (report, rowId): CellItem[] | null => report?.rows.find((row) => row.id === rowId)?.cellData.at(-1)?.items || null,
);

export const getNonTxnDeductionItems = createSelector(getRowCellDataItems, (cellData): CellItem[] | undefined =>
  cellData?.filter((cell) => !cell.txnId),
);

// ─── Net Worth chart filter selectors ───────────────────────────────────────

export type NetWorthChartFilter =
  | null
  | { kind: 'assets' }
  | { kind: 'debt' }
  | { kind: 'assetSubtype'; nodeId: string }
  | { kind: 'debtSubtype'; nodeId: string };

// A display option for the chart filter submenu — built from QCS tree children so business
// accounts and any other category QCS reports under 'OTHER' are always included.
export type NetWorthCategoryOption = {
  id: string; // QCS entity ID (e.g. 'OTHER', 'CASH & CHECKING')
  displayLabel: string; // Human-readable name from the QCS report row
};

// All selectors in this section share a common props shape so input selectors compose cleanly.
// Each selector only extracts the props it needs; unused props are silently ignored by reselect.
type NetWorthFilterProps = {
  location: string;
  timestep?: string;
  chartFilter: NetWorthChartFilter;
};

// Set of entity IDs currently represented in orderedFields. orderedFields are already keyed by
// entityId (see getMultilineChartDataFromReport), so this is a direct pass-through — no name
// lookup needed (a display-name lookup here previously collapsed distinct accounts that share
// a display name, e.g. a mortgage loan named after the same address as its property asset).
export const getNetWorthOrderedFieldIds = createSelector(
  getMultilineChartDataFromReport,
  ({ orderedFields }): Set<string> => new Set(orderedFields ?? []),
);

// Recursively collects leaf account keys from a net worth tree node. Uses getReportEntityKey so
// these keys line up with the ones getMultilineChartDataFromReport assigns to orderedFields —
// both must derive a row's identity the same way, or filtering silently breaks.
const collectLeafAccountIds = (node: NetWorthRow, ids: Set<string>): void => {
  if (!node.children || node.children.length === 0) {
    const key = getReportEntityKey(node);
    if (!node.isTopLevel && key) ids.add(key);
  } else {
    node.children.forEach((child) => collectLeafAccountIds(child, ids));
  }
};

// Extracts category options from a single top-level QCS tree node (ASSETS or LIABILITIES),
// filtering to only categories with at least one account in orderedFieldIds. Without this
// check, a category whose accounts are all isExcludedFromReports still appears in the menu
// but renders an empty chart when selected.
const categoryOptionsFromTreeNode = (
  node: NetWorthRow | undefined,
  orderedFieldIds: Set<string>,
): NetWorthCategoryOption[] =>
  (node?.children ?? [])
    .filter((categoryRow): categoryRow is typeof categoryRow & { entityId: string; name: string } => {
      if (!(categoryRow.entityId && categoryRow.name)) return false;
      const leafIds = new Set<string>();
      collectLeafAccountIds(categoryRow, leafIds);
      return [...leafIds].some((id) => orderedFieldIds.has(id));
    })
    .map((categoryRow) => ({ id: categoryRow.entityId, displayLabel: categoryRow.name }));

// Asset category options for the filter submenu — built directly from QCS tree children,
// the same source the BREAKDOWN card uses. Correctly includes 'OTHER' and business accounts
// (which live under OPERATIONAL-{businessId} in accountNodes and are missed by any approach
// that routes through accountNodesById).
export const getNetWorthRelevantAssetNodes = createSelector(
  getNetWorthNodeTree,
  getNetWorthOrderedFieldIds,
  (netWorthNodeTree, orderedFieldIds): NetWorthCategoryOption[] =>
    categoryOptionsFromTreeNode(
      netWorthNodeTree.find((n) => n.entityId === 'ASSETS'),
      orderedFieldIds,
    ),
);

// Debt category options for the filter submenu — built directly from QCS tree children.
export const getNetWorthRelevantDebtNodes = createSelector(
  getNetWorthNodeTree,
  getNetWorthOrderedFieldIds,
  (netWorthNodeTree, orderedFieldIds): NetWorthCategoryOption[] =>
    categoryOptionsFromTreeNode(
      netWorthNodeTree.find((n) => n.entityId === 'LIABILITIES'),
      orderedFieldIds,
    ),
);

// Account entity IDs belonging to the active filter (null = all accounts pass through).
export const getNetWorthIncludedAccountIds = createSelector(
  (_state: unknown, props: NetWorthFilterProps) => props.chartFilter,
  getNetWorthNodeTree,
  (chartFilter, netWorthNodeTree): Set<string> | null => {
    if (!chartFilter) return null;

    const topEntityId = chartFilter.kind === 'assets' || chartFilter.kind === 'assetSubtype' ? 'ASSETS' : 'LIABILITIES';
    const topNode = netWorthNodeTree.find((n) => n.entityId === topEntityId);
    if (!topNode) return new Set();

    const ids = new Set<string>();

    if (chartFilter.kind === 'assetSubtype' || chartFilter.kind === 'debtSubtype') {
      // Narrow to the single matching category node, then collect its leaf account IDs.
      const categoryNode = topNode.children?.find((categoryRow) => categoryRow.entityId === chartFilter.nodeId);
      if (categoryNode) collectLeafAccountIds(categoryNode, ids);
    } else {
      // Collect leaf account IDs across all categories under ASSETS or LIABILITIES.
      topNode.children?.forEach((categoryRow) => collectLeafAccountIds(categoryRow, ids));
    }

    return ids;
  },
);

// orderedFields narrowed to the accounts matching the current filter. orderedFields are
// already entityId-keyed, so we can intersect with includedAccountIds directly.
export const getNetWorthFilteredOrderedFields = createSelector(
  getMultilineChartDataFromReport,
  getNetWorthIncludedAccountIds,
  ({ orderedFields }, includedAccountIds): string[] => {
    const all = orderedFields ?? [];
    if (!includedAccountIds) return all;
    return all.filter((entityId) => includedAccountIds.has(entityId));
  },
);

// filteredOrderedFields sorted ascending by absolute value so the largest contributor is always created last
// (AmCharts stacks in creation order: last-created sits furthest from zero for both positive and negative series).
export const getNetWorthSortedFilteredFields = createSelector(
  getNetWorthFilteredOrderedFields,
  getMultilineChartDataFromReport,
  (filteredOrderedFields, { data }): string[] => {
    const lastDataPoint = data?.[data.length - 1];
    if (!lastDataPoint) return filteredOrderedFields;
    return [...filteredOrderedFields].sort((a, b) => {
      const aVal = typeof lastDataPoint[a] === 'number' ? (lastDataPoint[a] as number) : 0;
      const bVal = typeof lastDataPoint[b] === 'number' ? (lastDataPoint[b] as number) : 0;
      return Math.abs(aVal) - Math.abs(bVal);
    });
  },
);

// Finds a node in the tree by entityId (searches top-level and one level of children).
const findNetWorthRowByEntityId = (tree: NetWorthRow[], entityId: string): NetWorthRow | undefined => {
  for (const node of tree) {
    if (node.entityId === entityId) return node;
    const child = node.children?.find((c) => c.entityId === entityId);
    if (child) return child;
  }
  return undefined;
};

// Current/earliest totals and delta values for the active filter, sourced directly from QCS report rows.
export const getNetWorthFilteredTotals = createSelector(
  (_state: unknown, props: NetWorthFilterProps) => props.chartFilter,
  getNetWorthNodeTree,
  (chartFilter, netWorthNodeTree) => {
    if (!chartFilter) return null;

    const entityId =
      chartFilter.kind === 'assets'
        ? 'ASSETS'
        : chartFilter.kind === 'debt'
          ? 'LIABILITIES'
          : (accountNodeUtils.nodeIdToNetWorthEntityId[chartFilter.nodeId] ?? chartFilter.nodeId);

    const row = findNetWorthRowByEntityId(netWorthNodeTree, entityId);
    if (!row) return null;

    return {
      current: row.latestValue ?? 0,
      earliest: row.earliestValue ?? 0,
      deltaAmount: row.deltaAmount ?? 0,
      deltaPercentage: row.deltaPercentage ?? 0,
    };
  },
);

// Header label reflecting the active filter ("Your net worth", "Total assets", "Total Other", etc.).
export const getNetWorthFilteredHeaderLabel = createSelector(
  (_state: unknown, props: NetWorthFilterProps) => props.chartFilter,
  getNetWorthNodeTree,
  (chartFilter, netWorthNodeTree): string => {
    if (!chartFilter) return 'Your net worth';
    if (chartFilter.kind === 'assets') return 'Total assets';
    if (chartFilter.kind === 'debt') return 'Total debt';
    // chartFilter.kind is 'assetSubtype' or 'debtSubtype' here — look on the correct side only.
    const topEntityId = chartFilter.kind === 'assetSubtype' ? 'ASSETS' : 'LIABILITIES';
    const topNode = netWorthNodeTree.find((n) => n.entityId === topEntityId);
    const categoryRow = topNode?.children?.find((row) => row.entityId === chartFilter.nodeId);
    return `Total ${categoryRow?.name ?? ''}`;
  },
);

// All display values for the chart header and OverTimeLabel in one place.
// When a filter is active, uses QCS-sourced totals/deltas for that filter node.
// When no filter is active, falls back to the top-level "Net Worth" report row.
export const getNetWorthDisplayData = createSelector(
  (_state: unknown, props: NetWorthFilterProps) => props.chartFilter,
  getNetWorthNode,
  getNetWorthFilteredTotals,
  getNetWorthFilteredHeaderLabel,
  (chartFilter, netWorthNode, filteredTotals, headerLabel) => {
    if (chartFilter) {
      return {
        headerLabel,
        total: filteredTotals?.current ?? 0,
        earliest: filteredTotals?.earliest ?? 0,
        deltaAmount: filteredTotals?.deltaAmount,
        deltaPercentage: filteredTotals?.deltaPercentage,
      };
    }
    const cellData = netWorthNode?.cellData ?? [];
    const lastCell = cellData[cellData.length - 1];
    const firstCell = cellData[0];
    return {
      headerLabel: 'Your net worth',
      total: Number(lastCell?.value) || 0,
      earliest: Number(firstCell?.startValue) || 0,
      deltaAmount: netWorthNode?.deltaAmount != null ? Number(netWorthNode.deltaAmount) || 0 : undefined,
      deltaPercentage: netWorthNode?.deltaPercentage != null ? Number(netWorthNode.deltaPercentage) || 0 : undefined,
    };
  },
);

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getReportRow, getReportCellValue, getReportCellNumberValue);
